import { useCallback, useEffect, useState } from "react";
import { GraduationCap, Check, Undo2, ArrowUp, X } from "lucide-react";
import { toast } from "sonner";
import { useApi, errMsg } from "@/lib/api";
import { useSession } from "@/context/Session";
import { Markdown } from "@/components/Markdown";
import { t } from "@/i18n";

const STATUS = { not_started: ["Not started", "chip-muted"], learning: ["In progress", "chip-muted"], waiting_signoff: ["Waiting for sign-off", "chip-info"],
  signed_off: ["Signed off", "chip-ok"], needs_refresher: ["Needs refresher", "chip-err"], sent_back: ["Sent back", "chip-err"] };

export const TrainingBar = ({ plan }) => (
  <div className="stack-sm" data-testid={`training-bar-${plan.user_id}`}>
    <p className="text-sm font-semibold" data-testid="training-bar-label">{t("{name}: {n} of {total} skills signed off", { name: plan.name, n: plan.signed, total: plan.total })}</p>
    <div className="h-2 rounded-full bg-[var(--bg-surface-hover)] overflow-hidden"><div className="h-full bg-[var(--status-ok)] transition-[width] duration-500" style={{ width: `${plan.total ? (100 * plan.signed) / plan.total : 0}%` }} /></div>
  </div>
);

const Chip = ({ s, testid }) => <span className={`chip ${STATUS[s][1]}`} data-testid={testid}>{t(STATUS[s][0])}</span>;

function Quiz({ quiz, onSubmit }) {
  const [ans, setAns] = useState(quiz.map(() => -1));
  const [res, setRes] = useState(null);
  const submit = async () => setRes(await onSubmit(ans));
  return (
    <div className="stack-sm" data-testid="training-quiz">
      <p className="font-semibold">{t("Quiz")}</p>
      {quiz.map((q, i) => (
        <div key={i} className="card !p-4 stack-sm" data-testid={`quiz-question-${i}`}>
          <p className="font-medium">{i + 1}. {q.question}</p>
          {q.options.map((o, j) => (
            <label key={j} className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="radio" name={`q${i}`} checked={ans[i] === j} onChange={() => setAns(ans.map((a, k) => (k === i ? j : a)))} data-testid={`quiz-option-${i}-${j}`} /> {o}
            </label>
          ))}
          <p className="text-xs faint" data-testid={`quiz-source-${i}`}>{t("From SOP line {n}", { n: q.line })}: “{q.source_line}”</p>
          {res && <p className={`text-xs ${res.results[i].correct === res.results[i].chosen ? "text-[var(--status-ok)]" : "text-[var(--status-error)]"}`}>{res.results[i].explanation}</p>}
        </div>
      ))}
      <button className="btn btn-primary self-start" disabled={ans.includes(-1)} onClick={submit} data-testid="quiz-submit">{t(res ? "Retry" : "Submit answers")}</button>
      {res && <p className="font-semibold" data-testid="quiz-result">{t(res.passed ? "Passed: {pct}%. Waiting for sign-off." : "{pct}% — you need 80% to pass. Try again.", { pct: res.pct })}</p>}
    </div>
  );
}

function Skill({ sopId, onChanged }) {
  const api = useApi();
  const [d, setD] = useState(null);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState(null);
  useEffect(() => { setD(null); api.get(`/training/skills/${sopId}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, [api, sopId]);
  const ask = async (e) => {
    e.preventDefault();
    try { setAnswer((await api.post("/ask", { question })).data); } catch (err) { toast.error(errMsg(err)); }
  };
  const attempt = async (answers) => {
    const { data } = await api.post(`/training/skills/${sopId}/attempt`, { answers }); onChanged(); return data;
  };
  if (!d) return <p className="faint" data-testid="skill-loading">{t("Preparing the skill and quiz…")}</p>;
  return (
    <div className="stack" data-testid="training-skill">
      <div className="card"><Markdown testid="skill-sop">{d.sop.markdown}</Markdown></div>
      <form onSubmit={ask} className="flex gap-2"><input className="field" value={question} onChange={(e) => setQuestion(e.target.value)} placeholder={t("Ask a question about this skill…")} data-testid="skill-ask-input" /><button className="btn btn-ghost" disabled={!question.trim()} data-testid="skill-ask-submit">{t("Ask")}</button></form>
      {answer && <div className="card !p-4 text-sm" data-testid="skill-answer"><Markdown>{answer.answer_markdown}</Markdown>{answer.citations?.length > 0 && <p className="text-xs faint mt-2">{t("Sources")}: {answer.citations.map((c) => c.sop_title).join(", ")}</p>}</div>}
      <Quiz key={sopId} quiz={d.quiz} onSubmit={attempt} />
    </div>
  );
}

function SignOff({ plan, s, onDone }) {
  const api = useApi();
  const [note, setNote] = useState("");
  const go = async (approve) => {
    try { await api.post(`/training/${plan.user_id}/skills/${s.sop_id}/signoff`, { approve, note }); toast.success(t(approve ? "Signed off" : "Sent back")); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap gap-2 items-center">
      <input className="field !py-1.5 text-sm max-w-xs" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("Optional note")} data-testid={`signoff-note-${s.sop_id}`} />
      <button className="btn btn-primary btn-sm" onClick={() => go(true)} data-testid={`signoff-approve-${s.sop_id}`}><Check size={13} /> {t("Sign off")}</button>
      <button className="btn btn-ghost btn-sm" onClick={() => go(false)} data-testid={`signoff-sendback-${s.sop_id}`}><Undo2 size={13} /> {t("Send back")}</button>
    </div>
  );
}

function PlanEditor({ plan, onDone }) {
  const api = useApi();
  const [opts, setOpts] = useState([]);
  const [ids, setIds] = useState(plan.skills.map((s) => s.sop_id));
  const [add, setAdd] = useState("");
  useEffect(() => { api.get("/training/options").then((r) => setOpts(r.data)); }, [api]);
  const title = (id) => opts.find((o) => o.id === id)?.title || plan.skills.find((s) => s.sop_id === id)?.title || id;
  const save = async () => { try { await api.put(`/training/plans/${plan.user_id}`, { sop_ids: ids }); toast.success(t("Saved")); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="card !p-4 stack-sm" data-testid="plan-editor">
      {ids.map((id, i) => (
        <div key={id} className="flex items-center gap-2 text-sm"><span className="faint w-5">{i + 1}.</span><span className="flex-1">{title(id)}</span>
          {i > 0 && <button onClick={() => setIds(ids.map((x, k) => (k === i - 1 ? id : k === i ? ids[i - 1] : x)))} aria-label={t("Move up")}><ArrowUp size={13} /></button>}
          <button onClick={() => setIds(ids.filter((x) => x !== id))} aria-label={t("Remove")}><X size={13} /></button></div>
      ))}
      <div className="flex gap-2">
        <select className="field !py-1.5 text-sm" value={add} onChange={(e) => setAdd(e.target.value)} data-testid="plan-add-select">
          <option value="">{t("Add an SOP…")}</option>
          {opts.filter((o) => !ids.includes(o.id)).map((o) => <option key={o.id} value={o.id}>{o.title}{!o.tags?.includes("team") ? ` (${o.tags.map((x) => t(x[0].toUpperCase() + x.slice(1))).join(" + ")})` : ""}</option>)}
        </select>
        <button className="btn btn-ghost btn-sm" disabled={!add} onClick={() => { setIds([...ids, add]); setAdd(""); }} data-testid="plan-add-button">{t("Add")}</button>
        <button className="btn btn-primary btn-sm" onClick={save} data-testid="plan-save">{t("Save plan")}</button>
      </div>
    </div>
  );
}

function TraineeCard({ plan, admin, reload }) {
  const [edit, setEdit] = useState(false);
  return (
    <div className="card stack-sm" data-testid={`trainee-${plan.user_id}`}>
      <div className="flex items-start gap-3"><div className="flex-1"><TrainingBar plan={plan} />{plan.title && <p className="text-xs faint mt-1">{plan.title}</p>}</div>
        {admin && <button className="btn btn-ghost btn-sm" onClick={() => setEdit(!edit)} data-testid={`plan-edit-${plan.user_id}`}>{t(edit ? "Close" : "Edit plan")}</button>}</div>
      {edit && <PlanEditor plan={plan} onDone={() => { setEdit(false); reload(); }} />}
      {plan.skills.map((s, i) => (
        <div key={s.sop_id} className="border-t border-[var(--border-color)] pt-2 stack-sm" data-testid={`trainee-skill-${plan.user_id}-${i}`}>
          <div className="flex flex-wrap items-center gap-2 text-sm"><span className="faint">{i + 1}.</span><span className="font-medium">{s.title}</span><Chip s={s.status} testid={`trainee-skill-status-${i}`} />
            {s.signed_by && s.status === "signed_off" && <span className="text-xs faint">{t("by {name}", { name: s.signed_by })} · {s.signed_at.slice(0, 10)}</span>}</div>
          {s.status === "waiting_signoff" && <SignOff plan={plan} s={s} onDone={reload} />}
        </div>
      ))}
    </div>
  );
}

export function useTrainingPlans(enabled) {
  const api = useApi();
  const [plans, setPlans] = useState([]);
  const load = useCallback(() => { if (enabled) api.get("/training/plans").then((r) => setPlans(r.data)).catch(() => {}); }, [api, enabled]);
  useEffect(() => { load(); }, [load]);
  return [plans, load];
}

export default function Training() {
  const api = useApi();
  const { me } = useSession();
  const [mine, setMine] = useState(null);
  const [open, setOpen] = useState(null);
  const [plans, reload] = useTrainingPlans(me.is_maintainer);
  const load = useCallback(() => api.get("/training/me").then((r) => setMine(r.data)), [api]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="page stack max-w-4xl" data-testid="training-page">
      <h1 className="text-3xl md:text-4xl font-bold tracking-tight rise flex items-center gap-3"><GraduationCap /> {t("Training")}</h1>
      {mine && mine.total > 0 && (
        <div className="card stack rise d1" data-testid="my-training">
          <TrainingBar plan={mine} />
          {mine.skills.map((s, i) => (
            <button key={s.sop_id} className={`flex items-center gap-3 text-left p-3 rounded-xl transition-colors ${open === s.sop_id ? "bg-[var(--accent-subtle)]" : "hover:bg-[var(--bg-surface-hover)]"}`}
              onClick={() => setOpen(open === s.sop_id ? null : s.sop_id)} data-testid={`my-skill-${i}`}>
              <span className="faint w-5">{i + 1}.</span><span className="flex-1 font-medium">{s.title}</span><Chip s={s.status} testid={`my-skill-status-${i}`} />
            </button>
          ))}
          {open && <Skill sopId={open} onChanged={load} />}
        </div>
      )}
      {mine && mine.total === 0 && !me.is_maintainer && <p className="faint" data-testid="training-empty">{t("No training plan yet.")}</p>}
      {me.is_maintainer && <div className="stack rise d2" data-testid="trainee-list">
        <p className="text-lg font-semibold">{t("Trainees")}</p>
        {plans.map((p) => <TraineeCard key={p.user_id} plan={p} admin={me.role === "admin"} reload={() => { reload(); load(); }} />)}
        {plans.length === 0 && <p className="faint">{t("No trainees yet")}</p>}
      </div>}
    </div>
  );
}
